/**
 * SalesApprovalDetailPage — Phase 33.10.
 *
 * Full review page for a single approval item. Founder pastes a
 * signature from FounderSignBar, then approves, rejects, or edits.
 */

import { useState } from 'react';
import { Navigate, Link, useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiBaseUrl } from '@/api/http';
import { getAccessToken } from '@/api/supabase';
import type { ApprovalItem } from './SalesApprovalsPage';
import { statusColor, fmtQueued, urgencyLabel, itemTypeLabel } from './SalesApprovalsPage';

// ── Types ────────────────────────────────────────────────────────────────────

interface ApprovalItemDetail {
  item: ApprovalItem;
  full_body: string;
  pricing_json: string | null;
  gi_signature_hex: string | null;
  gi_pubkey_hex: string | null;
}

// ── Data ─────────────────────────────────────────────────────────────────────

async function fetchDetail(itemId: string): Promise<ApprovalItemDetail> {
  const token = await getAccessToken();
  const resp = await fetch(`${apiBaseUrl()}/sales/approvals/${itemId}/preview`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  return resp.json() as Promise<ApprovalItemDetail>;
}

async function postAction(
  itemId: string,
  action: 'approve' | 'reject' | 'edit',
  body: Record<string, string>,
  signature?: string,
): Promise<void> {
  const token = await getAccessToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(signature ? { 'X-Founder-Signature': signature } : {}),
  };
  const resp = await fetch(`${apiBaseUrl()}/sales/approvals/${itemId}/${action}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
}

// ── Styles ────────────────────────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid var(--omega-rim, rgba(255,255,255,0.12))',
  borderRadius: 4,
  color: 'var(--omega-ink)',
  fontFamily: 'var(--omega-mono)',
  fontSize: '8.5pt',
  padding: '4px 8px',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box' as const,
};

const btnStyle = (color: string): React.CSSProperties => ({
  background: `rgba(${hexToRgb(color)},0.12)`,
  border: `1px solid rgba(${hexToRgb(color)},0.4)`,
  borderRadius: 4,
  color,
  fontFamily: 'var(--omega-mono)',
  fontSize: '8.5pt',
  fontWeight: 600,
  padding: '6px 14px',
  cursor: 'pointer',
  letterSpacing: '0.04em',
});

function hexToRgb(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `${r},${g},${b}`;
}

// ── Toast ─────────────────────────────────────────────────────────────────────

function Toast({ msg, ok }: { msg: string; ok: boolean }) {
  return (
    <div style={{
      position: 'fixed',
      bottom: 24,
      left: '50%',
      transform: 'translateX(-50%)',
      background: ok ? 'rgba(52,211,153,0.15)' : 'rgba(248,113,113,0.15)',
      border: `1px solid ${ok ? '#34d399' : '#f87171'}`,
      borderRadius: 6,
      color: ok ? '#34d399' : '#f87171',
      fontFamily: 'var(--omega-mono)',
      fontSize: '9pt',
      padding: '10px 20px',
      zIndex: 9999,
      direction: 'ltr',
    }}>
      {msg}
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function SalesApprovalDetailPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const { item_id = '' } = useParams<{ item_id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isPending, isError } = useQuery({
    queryKey: ['sales', 'approvals', 'detail', item_id],
    queryFn: () => fetchDetail(item_id),
    refetchInterval: 30_000,
  });

  const [sigInput,       setSigInput]       = useState('');
  const [showCrypto,     setShowCrypto]     = useState(false);
  const [rejectOpen,     setRejectOpen]     = useState(false);
  const [rejectReason,   setRejectReason]   = useState('');
  const [editOpen,       setEditOpen]       = useState(false);
  const [editBody,       setEditBody]       = useState('');
  const [toast,          setToast]          = useState<{ msg: string; ok: boolean } | null>(null);
  const [acting,         setActing]         = useState(false);

  function showToast(msg: string, ok: boolean) {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3000);
  }

  async function handleApprove() {
    if (!sigInput.trim()) return;
    setActing(true);
    try {
      await postAction(item_id, 'approve', { nonce: crypto.randomUUID() }, sigInput.trim());
      await queryClient.invalidateQueries({ queryKey: ['sales', 'approvals'] });
      showToast('Item approved and sent successfully', true);
      setTimeout(() => navigate('/omega/sales/approvals'), 1500);
    } catch {
      showToast('Approval error — check signature and try again', false);
    } finally {
      setActing(false);
    }
  }

  async function handleReject() {
    if (!sigInput.trim() || !rejectReason.trim()) return;
    setActing(true);
    try {
      await postAction(item_id, 'reject', { reason: rejectReason.trim(), nonce: crypto.randomUUID() }, sigInput.trim());
      await queryClient.invalidateQueries({ queryKey: ['sales', 'approvals'] });
      showToast('Item rejected', true);
      setTimeout(() => navigate('/omega/sales/approvals'), 1500);
    } catch {
      showToast('Rejection error — try again', false);
    } finally {
      setActing(false);
    }
  }

  async function handleEdit() {
    if (!editBody.trim()) return;
    setActing(true);
    try {
      await postAction(item_id, 'edit', { updated_body: editBody.trim(), nonce: crypto.randomUUID() });
      await queryClient.invalidateQueries({ queryKey: ['sales', 'approvals'] });
      showToast('Item saved and re-entered the queue', true);
      setTimeout(() => navigate('/omega/sales/approvals'), 1500);
    } catch {
      showToast('Save error — try again', false);
    } finally {
      setActing(false);
    }
  }

  const item    = data?.item;
  const crumbLabel = item ? item.subject.slice(0, 40) + (item.subject.length > 40 ? '…' : '') : item_id.slice(0, 12);
  const isPending_ = item?.status === 'pending';

  return (
    <div className="omega-shell" style={{ fontFamily: 'var(--omega-mono)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
      <Breadcrumbs
        crumbs={[
          { label: 'OMEGA',         to: '/omega' },
          { label: 'Sales',         to: '/omega/sales' },
          { label: 'Approval queue',   to: '/omega/sales/approvals' },
          { label: crumbLabel },
        ]}
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, marginTop: 8 }}>
        <h1 style={{ margin: 0, fontSize: '12pt', fontWeight: 700, letterSpacing: '0.06em', direction: 'ltr', maxWidth: 480, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {item ? item.subject : 'Loading…'}
        </h1>
        {item && (
          <span style={{
            fontSize: '7.5pt',
            padding: '2px 8px',
            border: `1px solid ${statusColor(item.status)}`,
            color: statusColor(item.status),
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            fontWeight: 700,
            borderRadius: 3,
          }}>
            {item.status}
          </span>
        )}
        <Link
          to="/omega/sales/approvals"
          style={{
            marginLeft: 'auto',
            background: 'rgba(125,211,252,0.12)',
            border: '1px solid rgba(125,211,252,0.35)',
            borderRadius: 4,
            color: '#7dd3fc',
            fontFamily: 'var(--omega-mono)',
            fontSize: '8.5pt',
            fontWeight: 600,
            padding: '5px 12px',
            textDecoration: 'none',
          }}
        >
          ← Back to queue
        </Link>
      </div>

      {isPending && <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt' }}>Loading…</div>}
      {isError   && <div style={{ color: '#f87171', fontSize: '9pt' }}>Error loading details.</div>}

      {item && (
        <>
          {/* Meta row */}
          <div style={{
            display: 'flex',
            gap: 20,
            padding: '10px 0 14px',
            borderBottom: '1px solid var(--omega-rim, rgba(255,255,255,0.08))',
            flexWrap: 'wrap',
            direction: 'ltr',
          }}>
            <MetaChip label="Type"  value={itemTypeLabel(item.item_type)} />
            <MetaChip label="Lead"  value={item.lead_id} />
            <MetaChip label="Arrived" value={fmtQueued(item.queued_at)} />
            {urgencyLabel(item) && (
              <span style={{ color: '#f87171', fontWeight: 700, fontSize: '9pt', alignSelf: 'center' }}>
                {urgencyLabel(item)}
              </span>
            )}
          </div>

          {/* Full body preview */}
          <div style={{ marginTop: 18 }}>
            <div style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 8, direction: 'ltr' }}>
              Item content
            </div>
            {item.item_type === 'outreach' ? (
              <div style={{
                background: '#fff',
                borderRadius: 5,
                padding: '16px 20px',
                color: '#111',
                fontSize: '10pt',
                lineHeight: 1.6,
                whiteSpace: 'pre-wrap',
                fontFamily: 'Georgia, serif',
                direction: 'ltr',
              }}>
                {data?.full_body}
              </div>
            ) : (
              <pre style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid var(--omega-rim, rgba(255,255,255,0.1))',
                borderRadius: 5,
                padding: '14px 16px',
                fontSize: '8.5pt',
                lineHeight: 1.5,
                overflowX: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                color: 'var(--omega-ink)',
                direction: 'ltr',
              }}>
                {data?.full_body}
              </pre>
            )}
          </div>

          {/* Pricing */}
          {data?.pricing_json && (
            <div style={{ marginTop: 18 }}>
              <div style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 8, direction: 'ltr' }}>
                Pricing
              </div>
              <pre style={{
                background: 'rgba(251,191,36,0.06)',
                border: '1px solid rgba(251,191,36,0.2)',
                borderRadius: 5,
                padding: '12px 16px',
                fontSize: '8.5pt',
                lineHeight: 1.5,
                overflowX: 'auto',
                whiteSpace: 'pre-wrap',
                color: '#fbbf24',
              }}>
                {data.pricing_json}
              </pre>
            </div>
          )}

          {/* Crypto section — collapsed by default */}
          <div style={{ marginTop: 18 }}>
            <button
              onClick={() => setShowCrypto(v => !v)}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                fontFamily: 'var(--omega-mono)',
                fontSize: '8.5pt',
                color: 'var(--omega-ink-subtle)',
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
              }}
            >
              {showCrypto ? '▾' : '▸'} Cryptographic signature
            </button>
            {showCrypto && (
              <div style={{
                marginTop: 10,
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid var(--omega-rim, rgba(255,255,255,0.08))',
                borderRadius: 5,
                padding: '12px 14px',
              }}>
                <CryptoRow label="GI Signature" value={data?.gi_signature_hex} />
                <CryptoRow label="GI Pubkey"    value={data?.gi_pubkey_hex} />
              </div>
            )}
          </div>

          {/* Action section — only for pending items */}
          {isPending_ && (
            <div style={{
              marginTop: 24,
              padding: '18px 0 0',
              borderTop: '1px solid var(--omega-rim, rgba(255,255,255,0.1))',
            }}>
              {/* Signature input */}
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', letterSpacing: '0.06em', textTransform: 'uppercase', display: 'block', marginBottom: 6, direction: 'ltr' }}>
                  Signature (copy from-FounderSignBar ← Cmd+Shift+S)
                </label>
                <input
                  type="text"
                  placeholder="Paste the signature here…"
                  value={sigInput}
                  onChange={e => setSigInput(e.target.value)}
                  style={{ ...inputStyle, direction: 'ltr' }}
                  dir="ltr"
                />
              </div>

              {/* Primary action buttons */}
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
                <button
                  disabled={!sigInput.trim() || acting}
                  onClick={handleApprove}
                  style={{ ...btnStyle('#34d399'), opacity: !sigInput.trim() || acting ? 0.4 : 1, direction: 'ltr' }}
                >
                  Approve and send
                </button>
                <button
                  onClick={() => { setRejectOpen(v => !v); setEditOpen(false); }}
                  style={{ ...btnStyle('#f87171'), direction: 'ltr' }}
                >
                  Reject
                </button>
                <button
                  onClick={() => {
                    setEditOpen(v => !v);
                    setRejectOpen(false);
                    if (!editOpen) setEditBody(data?.full_body ?? '');
                  }}
                  style={{ ...btnStyle('#7dd3fc'), direction: 'ltr' }}
                >
                  Edit
                </button>
              </div>

              {/* Reject inline form */}
              {rejectOpen && (
                <div style={{ marginBottom: 14, padding: '14px', background: 'rgba(248,113,113,0.06)', border: '1px solid rgba(248,113,113,0.2)', borderRadius: 5 }}>
                  <label style={{ fontSize: '8.5pt', color: '#f87171', display: 'block', marginBottom: 6, direction: 'ltr' }}>
                    Rejection reason
                  </label>
                  <textarea
                    value={rejectReason}
                    onChange={e => setRejectReason(e.target.value)}
                    rows={3}
                    style={{ ...inputStyle, resize: 'vertical', direction: 'ltr' }}
                    dir="ltr"
                    placeholder="State the reason for rejection…"
                  />
                  <button
                    disabled={!sigInput.trim() || !rejectReason.trim() || acting}
                    onClick={handleReject}
                    style={{ ...btnStyle('#f87171'), marginTop: 8, opacity: (!sigInput.trim() || !rejectReason.trim() || acting) ? 0.4 : 1, direction: 'ltr' }}
                  >
                    Confirm rejection
                  </button>
                </div>
              )}

              {/* Edit inline form */}
              {editOpen && (
                <div style={{ marginBottom: 14, padding: '14px', background: 'rgba(125,211,252,0.06)', border: '1px solid rgba(125,211,252,0.2)', borderRadius: 5 }}>
                  <label style={{ fontSize: '8.5pt', color: '#7dd3fc', display: 'block', marginBottom: 6, direction: 'ltr' }}>
                    Edit content (returns to queue after saving)
                  </label>
                  <textarea
                    value={editBody}
                    onChange={e => setEditBody(e.target.value)}
                    rows={10}
                    style={{ ...inputStyle, resize: 'vertical', direction: 'ltr' }}
                    dir="ltr"
                  />
                  <button
                    disabled={!editBody.trim() || acting}
                    onClick={handleEdit}
                    style={{ ...btnStyle('#7dd3fc'), marginTop: 8, opacity: (!editBody.trim() || acting) ? 0.4 : 1, direction: 'ltr' }}
                  >
                    Save and return to queue
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {toast && <Toast msg={toast.msg} ok={toast.ok} />}
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function MetaChip({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ fontSize: '7.5pt', color: 'var(--omega-ink-subtle)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>{label}</span>
      <span style={{ fontSize: '9pt', color: 'var(--omega-ink)' }}>{value}</span>
    </div>
  );
}

function CryptoRow({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <span style={{ fontSize: '7.5pt', color: 'var(--omega-ink-subtle)', letterSpacing: '0.06em', textTransform: 'uppercase', marginRight: 8 }}>{label}</span>
      <span style={{ fontSize: '7.5pt', color: 'var(--omega-ink)', fontFamily: 'var(--omega-mono)', wordBreak: 'break-all' }}>
        {value ?? '—'}
      </span>
    </div>
  );
}
